import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getPasswordMatch, getPasswordRules } from '@/utils/validation';
import useApi from '@/hooks/useApi';
import useApp from '@/pages/App.context';
import FormPassword from '@/components/FormPassword';
import FormError from '@/components/FormError';
import Button from '@/components/Button';
import styles from '@/pages/Auth/Auth.module.scss';

type ResetPasswordInputs = {
  newPassword: string;
  repeatPassword: string;
};

const ResetPassword = () => {
  const { t } = useTranslation(['auth']);
  const { setAppTitle } = useApp();
  const navigate = useNavigate();
  const [loading] = useState(false);
  const [resetPassword] = useApi('users.password.reset');
  const [searchParams] = useSearchParams();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ResetPasswordInputs>({
    mode: 'onChange',
    criteriaMode: 'all',
  });

  const [newPasswordValue, setNewPasswordValue] = useState<string>('');

  const doResetPassword: SubmitHandler<ResetPasswordInputs> = async (data) => {
    localStorage.setItem('token', searchParams.get('token') ?? '');
    const resetPasswordResult = await resetPassword({
      password: data.newPassword,
    });
    localStorage.removeItem('token');
    if (!resetPasswordResult) {
      return;
    }
    if (resetPasswordResult.response?.success) {
      void navigate('/auth/reset-password/success');
    }
  };

  useEffect(() => {
    setAppTitle(t('auth:reset.title'));
  }, [setAppTitle, t]);

  // Drop any existing app session on arrival so the reset runs against the URL
  // token alone. The reset email belongs to whichever account its token
  // encodes — not necessarily the one currently signed in here — so we sign the
  // stale session out rather than letting it shadow the reset token (the
  // `users.password.reset` endpoint reads its bearer from localStorage).
  useEffect(() => {
    if (searchParams.get('token')) {
      localStorage.removeItem('token');
    }
  }, [searchParams]);

  return (
    <>
      <h2 className={styles.heading}>{t('auth:reset.title')}</h2>
      <div className={styles.text}>{t('auth:reset.resultDesc')}</div>
      <form onSubmit={handleSubmit(doResetPassword)} className={styles.form}>
        <FormPassword
          autoComplete="new-password"
          id="newPassword"
          label={t('auth:reset.newPassword')}
          placeholder={t('auth:reset.newPasswordPlaceholder')}
          withRequirements="newPassword"
          onValueChange={setNewPasswordValue}
          {...register('newPassword', getPasswordRules(t))}
        />
        <FormPassword
          autoComplete="new-password"
          id="repeatPassword"
          label={t('auth:reset.repeatPassword')}
          placeholder={t('auth:reset.repeatPasswordPlaceholder')}
          withRequirements="confirmPassword"
          confirmPasswordValue={newPasswordValue}
          {...register('repeatPassword', getPasswordMatch(t, newPasswordValue))}
        />
        {errors.root?.message && (
          <FormError isRoot message={errors.root.message} id="root-error" />
        )}
        <Button
          className={styles.button}
          variant="primary"
          submit
          disabled={!isValid}
          loading={loading}
        >
          {t('auth:reset.submit')}
        </Button>
      </form>
    </>
  );
};

export default ResetPassword;
